// Tandem — project hub: saved roots (name + path), search, create via desktop IPC

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RevoltButton,
  RevoltInput,
  RevoltModal,
} from "../../../components";
import { logger } from "../../../core";
import "./TandemChatInterface.css";

function getTandemDesktopApi() {
  if (typeof window === "undefined") return null;
  return window.tandemAPI || null;
}

function sortProjectsForSearch(projects, queryRaw) {
  const q = queryRaw.trim().toLowerCase();
  if (!q) {
    return [...projects].sort((a, b) => {
      const tb = (b.updatedAt || b.createdAt || "").toString();
      const ta = (a.updatedAt || a.createdAt || "").toString();
      return tb.localeCompare(ta);
    });
  }
  const matches = projects.filter((p) => {
    const name = (p.name || "").toLowerCase();
    const pathStr = (p.path || "").toLowerCase();
    return name.includes(q) || pathStr.includes(q);
  });
  return matches.sort((a, b) => {
    const qn = q;
    const an = (a.name || "").toLowerCase().includes(qn);
    const bn = (b.name || "").toLowerCase().includes(qn);
    if (an !== bn) return an ? -1 : 1;
    const ap = (a.path || "").toLowerCase().includes(qn);
    const bp = (b.path || "").toLowerCase().includes(qn);
    if (ap !== bp) return ap ? -1 : 1;
    const tb = (b.updatedAt || b.createdAt || "").toString();
    const ta = (a.updatedAt || a.createdAt || "").toString();
    return tb.localeCompare(ta);
  });
}

/**
 * @param {Object} props
 * @param {string} [props.directoryPath] — optional highlight for the row matching the open folder
 * @param {(path: string) => void} props.onDirectoryPathChange
 * @param {(meta?: { name?: string }) => void} props.onContinue
 */
export function TandemWorkingDirectoryStep({
  directoryPath = "",
  onDirectoryPathChange,
  onContinue,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createPath, setCreatePath] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const loadProjects = useCallback(async () => {
    const api = getTandemDesktopApi();
    if (!api || typeof api.listProjects !== "function") {
      setListError("Project list requires Revolt Desktop.");
      setProjects([]);
      setListLoading(false);
      return;
    }
    setListError("");
    setListLoading(true);
    try {
      const res = await api.listProjects();
      if (!res || !res.success) {
        setListError(res?.error || "Could not load projects.");
        setProjects([]);
        return;
      }
      setProjects(Array.isArray(res.projects) ? res.projects : []);
    } catch (err) {
      logger.error(
        { tags: "section|tandem|projects", color1: "red" },
        "listProjects failed:",
        err
      );
      setListError(err?.message || "Could not load projects.");
      setProjects([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const visibleProjects = useMemo(
    () => sortProjectsForSearch(projects, searchQuery),
    [projects, searchQuery]
  );

  const handleBrowseForCreate = useCallback(async () => {
    setCreateError("");
    try {
      const api = typeof window !== "undefined" ? window.dialogAPI : null;
      if (!api || typeof api.showOpenDialog !== "function") {
        setCreateError("Folder picker is only available inside Revolt Desktop.");
        return;
      }
      const result = await api.showOpenDialog({
        title: "Choose project folder",
        properties: ["openDirectory", "createDirectory"],
      });
      if (!result || result.canceled) return;
      const picked = Array.isArray(result.filePaths) ? result.filePaths[0] : null;
      if (picked) setCreatePath(picked);
    } catch (err) {
      setCreateError(err?.message || "Could not open folder picker.");
    }
  }, []);

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreateName("");
    setCreatePath("");
    setCreateError("");
    setCreateBusy(false);
  }, []);

  const handleCreateSave = useCallback(async () => {
    const api = getTandemDesktopApi();
    if (!api || typeof api.saveProject !== "function") {
      setCreateError("Saving requires Revolt Desktop.");
      return;
    }
    setCreateBusy(true);
    setCreateError("");
    try {
      const res = await api.saveProject({
        name: createName.trim(),
        path: createPath.trim(),
      });
      if (!res || !res.success) {
        setCreateError(res?.error || "Could not save project.");
        return;
      }
      closeCreateModal();
      await loadProjects();
    } catch (err) {
      setCreateError(err?.message || "Could not save project.");
    } finally {
      setCreateBusy(false);
    }
  }, [createName, createPath, closeCreateModal, loadProjects]);

  const openProject = useCallback(
    async (project) => {
      if (!project?.path) return;
      const api = getTandemDesktopApi();
      if (api && typeof api.saveProject === "function" && project.id) {
        try {
          await api.saveProject({
            id: project.id,
            name: project.name,
            path: project.path,
          });
        } catch {
          // non-fatal — still open folder
        }
        await loadProjects();
      }
      onDirectoryPathChange(project.path);
      onContinue({
        name: project.name || "",
        projectId: project.id || "",
      });
    },
    [onDirectoryPathChange, onContinue, loadProjects]
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    const api = getTandemDesktopApi();
    if (!api || typeof api.deleteProject !== "function") {
      setDeleteTarget(null);
      return;
    }
    setDeleteBusy(true);
    try {
      const res = await api.deleteProject({ id: deleteTarget.id });
      if (!res?.success) {
        logger.warn(
          { tags: "section|tandem|projects", color1: "yellow" },
          "deleteProject:",
          res?.error
        );
      }
      setDeleteTarget(null);
      await loadProjects();
    } catch (err) {
      logger.error(
        { tags: "section|tandem|projects", color1: "red" },
        "deleteProject failed:",
        err
      );
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, loadProjects]);

  const canSaveCreate =
    createName.trim().length > 0 && createPath.trim().length > 0 && !createBusy;

  return (
    <div className="tandem-chat-layout tandem-projects-hub">
      <div className="tandem-chat-layout__column">
        <div className="tandem-projects-hub__shell">
          <div className="tandem-projects-hub__toolbar">
            <RevoltInput
              className="tandem-projects-hub__search"
              variant="outline"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or path…"
              autoComplete="off"
              spellCheck={false}
              aria-label="Search projects"
            />
            <RevoltButton
              type="button"
              variant="accent"
              className="tandem-projects-hub__add"
              onClick={() => {
                setCreateError("");
                setShowCreateModal(true);
              }}
              title="New project"
              aria-label="New project"
            >
              <i className="fa-solid fa-plus" aria-hidden />
            </RevoltButton>
          </div>

          {listError ? (
            <p className="tandem-projects-hub__banner tandem-projects-hub__banner--error" role="alert">
              {listError}
            </p>
          ) : null}

          <div className="tandem-projects-hub__list" role="list">
            {listLoading ? (
              <div className="tandem-projects-hub__empty">Loading projects…</div>
            ) : visibleProjects.length === 0 ? (
              <div className="tandem-projects-hub__empty">
                {searchQuery.trim()
                  ? "No projects match your search."
                  : "No projects yet. Use + to add one."}
              </div>
            ) : (
              visibleProjects.map((p) => (
                <div
                  key={p.id || p.path}
                  className={
                    directoryPath && p.path === directoryPath
                      ? "tandem-projects-hub__row tandem-projects-hub__row--active"
                      : "tandem-projects-hub__row"
                  }
                  role="listitem"
                >
                  <button
                    type="button"
                    className="tandem-projects-hub__row-open"
                    onClick={() => openProject(p)}
                  >
                    <span className="tandem-projects-hub__name">{p.name || "Untitled"}</span>
                    <span className="tandem-projects-hub__path" title={p.path}>
                      {p.path}
                    </span>
                  </button>
                  <RevoltButton
                    type="button"
                    variant="outline"
                    className="tandem-projects-hub__row-delete"
                    title="Remove from list"
                    aria-label={`Delete project ${p.name || p.path}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(p);
                    }}
                  >
                    <i className="fa-solid fa-trash" aria-hidden />
                  </RevoltButton>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showCreateModal ? (
        <RevoltModal
          isOpen
          onClose={() => {
            if (!createBusy) closeCreateModal();
          }}
          title="New project"
          showCloseButton={!createBusy}
        >
          <div className="tandem-projects-hub__modal-body">
            <label className="tandem-projects-hub__field-label" htmlFor="tandem-new-project-name">
              Name
            </label>
            <RevoltInput
              id="tandem-new-project-name"
              variant="outline"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. My app"
              autoComplete="off"
            />
            <label className="tandem-projects-hub__field-label" htmlFor="tandem-new-project-path">
              Folder
            </label>
            <div className="tandem-projects-hub__path-row">
              <RevoltInput
                id="tandem-new-project-path"
                className="tandem-projects-hub__path-input"
                variant="outline"
                value={createPath}
                onChange={(e) => setCreatePath(e.target.value)}
                placeholder="Pick a folder…"
                autoComplete="off"
                spellCheck={false}
              />
              <RevoltButton
                type="button"
                variant="accent"
                className="tandem-projects-hub__browse"
                onClick={handleBrowseForCreate}
                disabled={createBusy}
                title="Choose folder"
                aria-label="Choose folder"
              >
                <i className="fa-solid fa-folder-open" aria-hidden />
              </RevoltButton>
            </div>
            {createError ? (
              <p className="tandem-projects-hub__modal-error" role="alert">
                {createError}
              </p>
            ) : null}
            <div className="tandem-projects-hub__modal-actions">
              <RevoltButton
                type="button"
                variant="outline"
                onClick={closeCreateModal}
                disabled={createBusy}
              >
                Cancel
              </RevoltButton>
              <RevoltButton
                type="button"
                variant="accent"
                onClick={handleCreateSave}
                disabled={!canSaveCreate}
              >
                Save
              </RevoltButton>
            </div>
          </div>
        </RevoltModal>
      ) : null}

      {deleteTarget ? (
        <RevoltModal
          isOpen
          onClose={() => {
            if (!deleteBusy) setDeleteTarget(null);
          }}
          title="Delete project"
          showCloseButton={!deleteBusy}
        >
          <div className="tandem-projects-hub__modal-body">
            <p className="tandem-projects-hub__delete-lead">
              Remove{" "}
              <strong>{deleteTarget.name || "this project"}</strong> from your list? Files on disk
              are not deleted.
            </p>
            <div className="tandem-projects-hub__modal-actions">
              <RevoltButton
                type="button"
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteBusy}
              >
                Cancel
              </RevoltButton>
              <RevoltButton
                type="button"
                variant="accent"
                onClick={confirmDelete}
                disabled={deleteBusy}
              >
                Delete
              </RevoltButton>
            </div>
          </div>
        </RevoltModal>
      ) : null}
    </div>
  );
}
